import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { api } from '../api/client';
import { useAuthStore } from '../stores/authStore';
import { usePageTitle } from '../hooks/usePageTitle';

interface LiteResult {
  overall_score: number;
  summary: string;
  top_strengths: string[];
  key_improvements: string[];
  verdict: string;
}

export function CvLite() {
  usePageTitle('Free CV Check');
  const { isAuthenticated } = useAuthStore();
  const [cvText, setCvText] = useState('');
  const [analysing, setAnalysing] = useState(false);
  const [result, setResult] = useState<LiteResult | null>(null);
  const [error, setError] = useState('');

  const handleAnalyse = async () => {
    if (cvText.trim().length < 100) {
      setError('Please paste at least 100 characters of your CV text.');
      return;
    }
    setAnalysing(true);
    setError('');

    try {
      const res = await api.post<LiteResult>('/cv/lite-check', { cv_text: cvText.trim() });
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed. Please try again.');
    } finally {
      setAnalysing(false);
    }
  };

  const scoreColor = (score: number) => {
    if (score >= 4) return 'text-green-600';
    if (score >= 3) return 'text-mustard-gold';
    return 'text-red-500';
  };

  return (
    <Layout>
      <div className="section py-12 sm:py-16">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-mustard-green-dark/5 rounded-full px-4 py-1.5 mb-4">
              <span className="w-2 h-2 rounded-full bg-mustard-green-mid animate-pulse" />
              <span className="text-xs font-semibold text-mustard-green-dark">Free Tool</span>
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-mustard-green-dark tracking-display mb-3">
              Quick CV Check
            </h1>
            <p className="text-slate-500 max-w-lg mx-auto">
              Paste your CV text below for a quick high-level assessment. For a comprehensive
              analysis with rewrite examples and role recommendations,
              {isAuthenticated ? (
                <Link to="/cv" className="text-mustard-green font-semibold ml-1">use the full CV Assessor</Link>
              ) : (
                <Link to="/register" className="text-mustard-green font-semibold ml-1">sign up for the full version</Link>
              )}.
            </p>
          </div>

          {result ? (
            /* Results */
            <div className="space-y-6">
              {/* Score */}
              <div className="card p-8 text-center">
                <p className="eyebrow mb-2">Your CV Score</p>
                <div className={`font-display text-5xl font-bold ${scoreColor(result.overall_score)}`}>
                  {result.overall_score}<span className="text-2xl text-slate-300">/5</span>
                </div>
                <p className="text-sm text-slate-500 mt-2 font-medium">{result.verdict}</p>
              </div>

              {/* Summary */}
              <div className="card p-6">
                <h2 className="font-display text-lg font-bold text-mustard-green-dark mb-2">Summary</h2>
                <p className="text-sm text-slate-600 leading-relaxed">{result.summary}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Strengths */}
                <div className="card p-6">
                  <h3 className="font-display font-bold text-mustard-green-dark mb-3 text-sm">Strengths</h3>
                  <ul className="space-y-2">
                    {result.top_strengths.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                        <span className="text-green-500 mt-0.5 flex-shrink-0">&#10003;</span>{s}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Improvements */}
                <div className="card p-6">
                  <h3 className="font-display font-bold text-mustard-green-dark mb-3 text-sm">Key Improvements</h3>
                  <ul className="space-y-2">
                    {result.key_improvements.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                        <span className="text-mustard-gold mt-0.5 flex-shrink-0">&#9679;</span>{s}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Upsell */}
              <div className="bg-mustard-green-dark rounded-2xl p-6 sm:p-8 text-center">
                <h3 className="font-display text-lg font-bold text-white mb-2">Want the full picture?</h3>
                <p className="text-white/60 text-sm mb-6 max-w-md mx-auto">
                  The full CV Assessor provides detailed 7-Point Framework scoring, before/after rewrite examples,
                  role-specific recommendations, and a prioritised action plan.
                </p>
                {isAuthenticated ? (
                  <Link to="/cv" className="btn-gold inline-block">Full CV Assessment</Link>
                ) : (
                  <Link to="/register" className="btn-gold inline-block">Sign Up — It's Free</Link>
                )}
              </div>

              <button onClick={() => { setResult(null); setCvText(''); }} className="text-sm text-mustard-green font-semibold hover:underline">
                &#8592; Check another CV
              </button>
            </div>
          ) : (
            /* Input form */
            <div>
              <div className="card p-6 sm:p-8">
                <label className="block text-sm font-semibold text-mustard-charcoal mb-2">
                  Paste your CV text
                </label>
                <textarea
                  value={cvText}
                  onChange={(e) => setCvText(e.target.value)}
                  rows={12}
                  placeholder="Copy and paste the text content of your CV here..."
                  className="input resize-none font-mono text-xs leading-relaxed"
                />
                <p className="text-xs text-slate-400 mt-2">
                  {cvText.length} characters &middot; We recommend pasting at least your professional summary, experience, and skills sections.
                </p>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg mt-4 text-sm">
                    {error}
                  </div>
                )}

                <button
                  onClick={handleAnalyse}
                  disabled={analysing || cvText.trim().length < 100}
                  className="btn-primary w-full mt-4 !py-3.5"
                >
                  {analysing ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Analysing...
                    </span>
                  ) : 'Analyse My CV'}
                </button>
              </div>

              <p className="text-center text-xs text-slate-400 mt-6">
                Your CV text is not stored. This is a one-time analysis.
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
